import api from "../../../Services/api";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import MenuLateral from "../../../Componentes/MenuLateral";
import MenuSuperior from "../../../Componentes/MenuSuperior";
import { useAuthContext } from "../../../context/authContext";
import { useOperador } from "../../../Hooks/useOperador";
import { useNavigate, useParams } from "react-router-dom";
import debounce from "lodash.debounce";
import { FiSearch, FiX, FiChevronLeft, FiChevronRight, FiMove, FiCheck } from "react-icons/fi";
import { IoReturnDownBackOutline } from "react-icons/io5";
import { MdPlaylistAdd } from "react-icons/md";
import AccordionSecao from "../../../Componentes/chkSecao/accordionSecao";
import CadastroSecaoModal from "./cadastroSecaoModal";
import Swal from "sweetalert2";
import "./index.css";

const AuditoriaSecao = () => {

  const { IDClienteAuditoria } = useParams();
  const [cliente, setCliente] = useState(null);
  const [secoes, setSecoes] = useState([]);
  const [total, setTotal] = useState(0);

  const [showModalSecao, setShowModalSecao] = useState(false);
  const [secaoEditando, setSecaoEditando] = useState(null); // null = nova seção

  const [page, setPage] = useState(1);
  const limit = 20;

  // filtro digitado e filtro aplicável são separados
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");

  const [openAccordion, setOpenAccordion] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isReordenando, setIsReordenando] = useState(false);
  const [secoesOrdenadas, setSecoesOrdenadas] = useState([]);
  const [salvandoOrdem, setSalvandoOrdem] = useState(false);

  const { authReady } = useAuthContext();
  const { IDOperador, nome, operador } = useOperador();

  const navigate = useNavigate();
  let idFormatado = String(IDOperador).padStart(6, '0');
  const clienteId = Number(IDClienteAuditoria);
  const podeReordenar = !debouncedFilter && secoes.length > 1;

  const isAdmin = useMemo(() => {
    if (!operador) return false;

    // Administrador real
    if (operador.permissaoPrincipal === 99) return true;

    // Gerente Auditoria (admin funcional da tela)
    if (
      operador.permissaoPrincipal === 5 &&
      operador.cargos?.some((c) => c.descricao === "Gerente Auditoria")
    ) {
      return true;
    }

    return false;
  }, [operador]);



  const ordenarPorOrdem = useCallback((lista) => {
    return [...lista].sort((a, b) => {
      const ordemA = a.ordem ?? 0;
      const ordemB = b.ordem ?? 0;
      if (ordemA !== ordemB) return ordemA - ordemB;
      return (a.IDSecao ?? 0) - (b.IDSecao ?? 0);
    });
  }, []);

  const secoesOrdenadasPorOrdem = useMemo(() => {
    return ordenarPorOrdem(secoes);
  }, [secoes, ordenarPorOrdem]);

  const handleDebounce = useCallback(
    debounce((value) => {
      setDebouncedFilter(value);
      setPage(1); // Sempre volta pra primeira página quando filtra
    }, 500),
    []
  );

  // Quando o usuário digita -> dispara debounce
  const onChangeFiltro = (e) => {
    const value = e.target.value;
    setFilter(value);
    handleDebounce(value);
  };

  const handleVoltar = () => {
    navigate("/Servicos/" + clienteId);
  };

  useEffect(() => {
    if (authReady) {
      if (!operador) {
        navigate("/");
      } else {
        buscaCliente(clienteId);
      }
    }
  }, [authReady, operador, clienteId]);

  useEffect(() => {
    if (!cliente) return;

    fetchSecoes(clienteId);
  }, [page, debouncedFilter, cliente]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilter]);

  useEffect(() => {
    if (isReordenando) {
      setSecoesOrdenadas(secoesOrdenadasPorOrdem);
      setOpenAccordion(null);
    }
  }, [isReordenando, secoesOrdenadasPorOrdem]);

  useEffect(() => {
    if (isReordenando && !podeReordenar) {
      setIsReordenando(false);
      setSecoesOrdenadas([]);
    }
  }, [isReordenando, podeReordenar]);


  useEffect(() => {
    return () => {
      handleDebounce.cancel();
    };
  }, [handleDebounce]);

  const clearFilter = () => {
    setFilter("");
    setDebouncedFilter("");
    setPage(1);
  };

  const abrirModalNovaSecao = () => {
    if (!isAdmin) return;
    setSecaoEditando(null);
    setShowModalSecao(true);
  };

  const abrirModalEditarSecao = (IDSecao) => {
    setSecaoEditando(IDSecao);
    setShowModalSecao(true);
  };

  const iniciarReordenacao = () => {
    if (!isAdmin || !podeReordenar) return;
    setIsReordenando(true);
    setSecoesOrdenadas(secoesOrdenadasPorOrdem);
  };

  const cancelarReordenacao = () => {
    setIsReordenando(false);
    setSecoesOrdenadas([]);
  };

  const moverSecao = (secaoId, direction) => {
    setSecoesOrdenadas((prev) => {
      const index = prev.findIndex((item) => item.IDSecao === secaoId);
      if (index < 0) return prev;
      const destino = index + direction;
      if (destino < 0 || destino >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(destino, 0, item);
      return next;
    });
  };

  const salvarReordenacao = async () => {
    if (!isAdmin || !secoesOrdenadas.length) return;

    try {
      setSalvandoOrdem(true);
      Swal.fire({
        title: "Salvando ordem...",
        text: "Aguarde enquanto atualizamos as seções.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const atualizadas = secoesOrdenadas.map((secao, index) => ({
        ...secao,
        ordem: index + 1,
      }));

      await Promise.all(
        atualizadas.map((secao) =>
          api.put(`/chksecao/${secao.IDSecao}`, {
            IDCliente: secao.IDCliente ?? clienteId,
            descricao: secao.descricao,
            ordem: secao.ordem ?? null,
            situacao: secao.situacao ?? 1,
          })
        )
      );

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Ordem salva!",
        text: "A nova ordem foi aplicada com sucesso.",
      });

      setIsReordenando(false);
      setSecoesOrdenadas([]);
      fetchSecoes(clienteId);
    } catch (error) {
      console.error("Falha ao salvar ordem das seções", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível salvar a nova ordem.",
      });
    } finally {
      setSalvandoOrdem(false);
    }
  };



  const buscaCliente = async (IDCliente) => {
    if (!IDCliente) return null;
    try {
      const res = await api.get(`/cliente/${IDCliente}`);
      setCliente(res.data.Cliente);
    } catch (err) {
      console.error("Erro ao buscar cliente:", err);
      setCliente(null);
    }
  };

  const buscaSecoesCliente = async ({
    page = 1,
    limit = 20,
    IDCliente,
    filter = "",
    operador,
  }) => {
    const params = {
      page,
      limit,
      IDCliente,
      filter,
    };

    // Se NÃO for administrador, filtra pelas seções que o operador tem acesso
    const isAdmin =
      operador?.permissaoPrincipal === 99 ||
      operador?.cargos?.some((c) => c.descricao === "Administrador");

    if (!isAdmin && operador?.IDOperador) {
      params.IDOperador = operador.IDOperador;
    }

    const response = await api.get("/chksecoesstats", { params });
    return response.data;
  };


  async function fetchSecoes(IDCliente) {
    try {
      setLoading(true);

      const result = await buscaSecoesCliente({
        page,
        limit,
        IDCliente,
        filter: debouncedFilter,
        operador
      });

      setSecoes(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error("Erro ao carregar seções", error);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / limit);
  const listaExibida = isReordenando ? secoesOrdenadas : secoesOrdenadasPorOrdem;

  return (
    <div className="menu-container-menu">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <MenuSuperior userId={idFormatado} userName={nome} />

        <div className="mb-4">
          <h4 style={{ width: '100%', textAlign: 'center', paddingTop: '30px' }}>
            AUDITORIA
          </h4>
          <h1>{cliente?.nome} - {cliente?.uf}</h1>
        </div>

        <div className="container mt-2 px-5">
          <div className="menuListArea">
            <div className="btn-voltarArea">
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm btn-voltar"
                  onClick={handleVoltar}
                >
                  <IoReturnDownBackOutline size={18} style={{ marginRight: 6 }} />
                  Voltar
                </button>
              </div>
            </div>
            <div className="search-container">
              <span className="search-icon">
                <FiSearch />
              </span>
              <input
                type="text"
                className="search-input"
                placeholder="Buscar seção..."
                value={filter}
                onChange={onChangeFiltro}
                disabled={isReordenando || salvandoOrdem}
              />
              {filter.length > 0 && (
                <button className="clear-btn" onClick={clearFilter} disabled={isReordenando || salvandoOrdem}>
                  <FiX />
                </button>
              )}
            </div>

            <div className="mainMenuList">
              <div className="actions">
                {isAdmin && (
                  <>
                    <button
                      className="btn-primary btnMainMenu"
                      onClick={abrirModalNovaSecao}
                      disabled={isReordenando || salvandoOrdem}
                    >
                      <MdPlaylistAdd style={{ fontSize: 20 }} /> Nova seção
                    </button>

                    {!isReordenando && (
                      <button
                        className="btn-primary btnMainMenu"
                        onClick={iniciarReordenacao}
                        disabled={!podeReordenar || loading}
                      >
                        <FiMove style={{ fontSize: 20 }} /> Reordenar
                      </button>
                    )}

                    {isReordenando && (
                      <>
                        <button
                          className="btn-primary btnMainMenu"
                          onClick={salvarReordenacao}
                          disabled={salvandoOrdem}
                        >
                          <FiCheck style={{ fontSize: 20 }} /> Salvar ordem
                        </button>

                        <button
                          className="btn-secondary btn-cancel-reorder btnMainMenu"
                          onClick={cancelarReordenacao}
                          disabled={salvandoOrdem}
                        >
                          <FiX style={{ fontSize: 20 }} /> Cancelar
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {loading && <p className="loading-text">Carregando...</p>}

          {!loading && !secoes.length && (
            <p className="empty-text">Nenhuma seção encontrada.</p>
          )}
          <ul className="secao-list">
            {listaExibida.map((secao, index) => (
              <AccordionSecao
                key={secao.IDSecao}
                secao={secao}
                isOpen={openAccordion === secao.IDSecao}
                isReordenando={isReordenando}
                onMoveUp={() => moverSecao(secao.IDSecao, -1)}
                onMoveDown={() => moverSecao(secao.IDSecao, 1)}
                disableMoveUp={index === 0 || salvandoOrdem}
                disableMoveDown={index === listaExibida.length - 1 || salvandoOrdem}
                operador={operador}
                onToggle={() => {
                  if (!isReordenando) {
                    setOpenAccordion(
                      openAccordion === secao.IDSecao ? null : secao.IDSecao
                    );
                  }
                }}
                onEdit={() => abrirModalEditarSecao(secao.IDSecao)}
              />
            ))}
          </ul>

          {/* Paginação */}
          <div className="pagination">
            <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1 || isReordenando}>
              <FiChevronLeft size={18} style={{ marginRight: 4 }} />
            </button>

            <span>Página {page} de {totalPages}</span>

            <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages || isReordenando}>
              <FiChevronRight size={18} style={{ marginLeft: 4 }} />
            </button>
          </div>
        </div>

      </div>
      {showModalSecao && (
        <CadastroSecaoModal
          open={showModalSecao}
          IDCliente={clienteId}
          clienteNome={cliente?.nome}
          clienteUf={cliente?.uf}
          totalSecoes={total}
          IDSecao={secaoEditando} // null = novo
          onClose={() => setShowModalSecao(false)}
          onSaved={() => {
            setShowModalSecao(false);
            fetchSecoes(clienteId); // Recarrega lista após salvar
          }}
        />
      )}
    </div>
  );
};

export default AuditoriaSecao;


